import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, MessageCircleQuestion, Sparkles } from "lucide-react";
import { SURVEY_BANK, type SurveyCategory, type SurveyQuestion } from "@/lib/polls/surveyBank";
import { recordSurveyResponse } from "@/lib/polls/surveyIntelligence";
import { upsertSurveyInsights } from "@/lib/fanProfile.functions";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";

type Window = SurveyQuestion["windows"][number];

interface Props {
  /** Categories allowed on this surface (kept narrow on purpose). */
  categories: SurveyCategory[];
  /** Match window for filtering. */
  window: Window;
  /** Optional label for the eyebrow strip. */
  label?: string;
  /** Localstorage namespace so different surfaces don't share throttles. */
  storageKey: string;
  /** Minimum gap between questions on the same surface (default 90s). */
  minIntervalMs?: number;
  matchId?: string;
  city?: string;
}

interface ThrottleState { lastShownAt: number; seenIds: string[] }

function readThrottle(key: string): ThrottleState {
  if (typeof window === "undefined") return { lastShownAt: 0, seenIds: [] };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { lastShownAt: 0, seenIds: [] };
    const parsed = JSON.parse(raw) as ThrottleState;
    return { lastShownAt: parsed.lastShownAt ?? 0, seenIds: parsed.seenIds ?? [] };
  } catch { return { lastShownAt: 0, seenIds: [] }; }
}

function writeThrottle(key: string, state: ThrottleState): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, JSON.stringify(state)); } catch { /* noop */ }
}

function pickQuestion(categories: SurveyCategory[], win: Window, seen: string[]): SurveyQuestion | null {
  const pool = SURVEY_BANK.filter((q) => categories.includes(q.category) && q.windows.includes(win));
  if (pool.length === 0) return null;
  const fresh = pool.filter((q) => !seen.includes(q.id));
  const final = fresh.length > 0 ? fresh : pool;
  return final[Math.floor(Math.random() * final.length)];
}

export function SmartSurveyDeck({
  categories, window: win, label, storageKey, minIntervalMs = 90_000, matchId, city,
}: Props) {
  const [throttle, setThrottle] = useState<ThrottleState>(() => readThrottle(storageKey));
  const [question, setQuestion] = useState<SurveyQuestion | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const eligible = useMemo(() => {
    const now = Date.now();
    return now - throttle.lastShownAt >= minIntervalMs;
  }, [throttle, minIntervalMs]);

  useEffect(() => {
    if (!eligible || question || done) return;
    const q = pickQuestion(categories, win, throttle.seenIds);
    if (q) {
      setQuestion(q);
      void trackEvent("smart_survey_shown", { id: q.id, category: q.category, surface: storageKey });
    }
  }, [eligible, question, done, categories, win, throttle.seenIds, storageKey]);

  if (!question || done) {
    if (done) {
      return (
        <div className="flex items-center gap-2 rounded-xl border border-[oklch(0.82_0.24_145)]/40 bg-[oklch(0.82_0.24_145)]/10 px-3 py-2 text-[11px] font-bold text-[oklch(0.82_0.24_145)]">
          <CheckCircle2 className="h-3.5 w-3.5" /> ¡Gracias por tu respuesta!
        </div>
      );
    }
    return null;
  }

  const submit = async (answer: string) => {
    setSubmitting(true);
    try {
      recordSurveyResponse({
        question, answer, match_id: matchId, city,
        device: typeof navigator !== "undefined" && /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
      });
      // Feed Fan DNA when applicable.
      if (question.category === "food_beverage" || question.category === "brand_preference") {
        const isDrink = /toma|bebida|drink/i.test(question.question);
        const isBrand = question.category === "brand_preference";
        await upsertSurveyInsights({
          data: {
            session_id: getSessionId(),
            food_preference: !isDrink && !isBrand ? answer : null,
            drink_preference: isDrink ? answer : null,
            favorite_brand: isBrand ? answer : null,
            purchase_intent: "medium",
          },
        }).catch(() => null);
      }
      void trackEvent("smart_survey_answered", { id: question.id, category: question.category, surface: storageKey });
    } catch (e) {
      console.warn("[SmartSurveyDeck] submit failed", e);
    }
    const nextState: ThrottleState = {
      lastShownAt: Date.now(),
      seenIds: [...throttle.seenIds, question.id].slice(-20),
    };
    writeThrottle(storageKey, nextState);
    setThrottle(nextState);
    setDone(true);
    setSubmitting(false);
  };

  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[oklch(0.82_0.24_145)]">
        <MessageCircleQuestion className="h-3 w-3" /> {label ?? "Pulso de la afición"}
      </div>
      <div className="mt-1.5 text-sm font-black leading-snug">{question.question}</div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {question.options.map((opt) => (
          <button
            key={opt}
            disabled={submitting}
            onClick={() => void submit(opt)}
            className="rounded-lg border border-border bg-background/40 px-2 py-1.5 text-[11px] font-bold transition hover:border-[oklch(0.82_0.24_145)]/60 hover:bg-[oklch(0.82_0.24_145)]/10 disabled:opacity-50"
          >
            {opt}
          </button>
        ))}
      </div>
      {question.rewardEligible && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[oklch(0.85_0.18_85)]">
          <Sparkles className="h-3 w-3" /> Suma puntos al responder
        </div>
      )}
    </div>
  );
}